import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ReferralMaterialsDto,
  UpdateReferralMaterialDto,
} from 'api/ClientArea/ReferralMaterialsDto'
import { Language } from 'api/Clients/Types/Language'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { AuthSessionContext } from 'utils/AuthContext'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'

import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { errorToast, ToastContext } from '../../../components/Toast/Toast'
import { isTickmillCompanyDisabled } from '../../../utils/isTickmillCompanyDisabled'

export interface ReferralMaterialsDetailsFormValues {
  name: string | undefined
  caLanguages: string[]
  typeId: number | undefined
  categoryIds: string[]
  enabled: boolean
  endDateTime: string | undefined
  tickmillCompanyId: number | undefined
  shouldDeactivatePromoAssets: boolean | undefined
  activePromoAssetsLanguages: NameDto<string>[] | undefined
}

const ReferralMaterialsFormUI: React.FC<
  FormikProps<ReferralMaterialsDetailsFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setFieldValue, values, errors, data, resetForm, isValid } = props
  const {
    languages,
    tickmillCompanies,
    referralMaterialsCategoriesByEntity,
    referralMaterialsTypes,
    fetchReferralMaterialsCategoriesByEntity,
  } = props

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)

  const [isEdit, setEdit] = useState(false)
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false)
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
  const previousTickmillCompanyIdRef = useRef<number | undefined>()

  const dateFormatType = auth?.user?.dateFormatType?.name
  const languageOptions = useMemo(
    () => languages.map((language) => ({ id: language.id, name: language.name })),
    [languages]
  )
  const typeOptions = useMemo(
    () => referralMaterialsTypes.map((type) => ({ id: type.id, name: type.name })),
    [referralMaterialsTypes]
  )
  const companyOptions = useMemo(
    () =>
      tickmillCompanies.map((company) => ({
        id: company.id,
        name: company.name,
        disabled: isTickmillCompanyDisabled(company),
      })),
    [tickmillCompanies]
  )
  const categoryOptions = useMemo(
    () =>
      (referralMaterialsCategoriesByEntity ?? []).map((category) => ({
        id: category.id,
        name: category.name,
        disabled: !category.isActive && !values.categoryIds.includes(category.id),
      })),
    [referralMaterialsCategoriesByEntity, values.categoryIds]
  )

  const isEndDateExpired = useMemo(() => {
    if (!values.endDateTime) {
      return false
    }

    return createDateAsUTC(values.endDateTime, false, true) < new Date()
  }, [values.endDateTime])

  useEffect(() => {
    if (values.tickmillCompanyId == null) {
      previousTickmillCompanyIdRef.current = undefined
      return
    }

    const hasCompanyChanged = previousTickmillCompanyIdRef.current !== values.tickmillCompanyId
    previousTickmillCompanyIdRef.current = values.tickmillCompanyId

    if (!hasCompanyChanged) {
      return
    }

    if (isEdit) {
      void setFieldValue('categoryIds', [])
    }

    setIsCategoriesLoading(true)
    fetchReferralMaterialsCategoriesByEntity(values.tickmillCompanyId).finally(() =>
      setIsCategoriesLoading(false)
    )
  }, [fetchReferralMaterialsCategoriesByEntity, isEdit, setFieldValue, values.tickmillCompanyId])

  const handleSubmitForm = () => {
    const removedLanguages = data.caLanguages.filter(
      (language) => !values.caLanguages.includes(language.id)
    )

    if (removedLanguages.length > 0) {
      const activeLanguageIds =
        values.activePromoAssetsLanguages?.map((language) => language.id) ?? []
      const isRemovedLanguageInUse = removedLanguages.some((language) =>
        activeLanguageIds.includes(language.id)
      )

      if (isRemovedLanguageInUse) {
        setIsConfirmationModalVisible(true)
        return
      }
    }

    handleSubmit()
  }

  const handleConfirmationModalConfirm = () => {
    setFieldValue('shouldDeactivatePromoAssets', true).then(() => {
      handleSubmit()
      setIsConfirmationModalVisible(false)
    })
  }

  const handleConfirmationModalCancel = () => {
    setToast(errorToast('Material not saved'))
    setIsConfirmationModalVisible(false)
    handleCancel()
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  return (
    <>
      <div className='px-4 md:px-6'>
        <Form>
          <DataGrid
            rightAddon={
              <ButtonGroup>
                {isEdit ? (
                  <>
                    <Button
                      type='button'
                      variant='outline'
                      className='is-danger'
                      onClick={handleCancel}
                      data-test='cancel-button'
                    >
                      Cancel
                    </Button>
                    <Button
                      type='button'
                      disabled={!isValid}
                      onClick={handleSubmitForm}
                      data-test='save-button'
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <Button type='button' onClick={() => setEdit(true)} data-test='edit-button'>
                    Edit
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        type='button'
                        variant='outline'
                        size='icon'
                        aria-label='More options'
                        className='more-button'
                      />
                    }
                  >
                    <ChevronDown />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      onSelect={() => props.setAuditLogModal(true)}
                      data-test='audit-log-menu-button'
                    >
                      Audit Log
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            }
          >
            <DataColumn>
              <DataCard
                title={!isEdit ? 'Referral Material Page' : 'Edit Referral Material Page'}
                columns={2}
              >
                <FormField name='name' error={errors.name} label='Name' readOnly={!isEdit} />
                <FormMultiSelectField
                  error={errors.caLanguages?.toString()}
                  name='caLanguages'
                  label='CA Language'
                  value={values.caLanguages}
                  options={languageOptions}
                  readOnly={!isEdit}
                  showSearch
                  allowClear
                  onChange={(value) =>
                    setFieldValue(
                      'caLanguages',
                      Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
                    )
                  }
                />
                <FormSelectField
                  error={errors.typeId}
                  name='typeId'
                  label='Type'
                  value={values.typeId}
                  options={typeOptions}
                  readOnly={!isEdit}
                  showSearch
                  allowClear
                  onChange={(value) => setFieldValue('typeId', value ? Number(value) : undefined)}
                />
                <FormSelectField
                  name='tickmillCompanyId'
                  error={errors.tickmillCompanyId}
                  label='Tickmill Entity'
                  value={values.tickmillCompanyId}
                  options={companyOptions}
                  readOnly={!isEdit}
                  showSearch
                  allowClear
                  onChange={(value) =>
                    setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
                  }
                />
                <FormMultiSelectField
                  error={errors.categoryIds?.toString()}
                  name='categoryIds'
                  label='Category'
                  value={values.categoryIds}
                  options={categoryOptions}
                  readOnly={!isEdit}
                  disabled={isCategoriesLoading}
                  showSearch
                  allowClear
                  onChange={(value) =>
                    setFieldValue(
                      'categoryIds',
                      Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
                    )
                  }
                />
                <FormDateField
                  error={errors.endDateTime}
                  name='endDateTime'
                  label='End Date'
                  readOnly={!isEdit}
                  dateFormatType={dateFormatType}
                />
                <FormCheckboxField
                  name='enabled'
                  label='Active'
                  error={errors.enabled}
                  value={values.enabled}
                  readOnly={!isEdit}
                  disabled={isEndDateExpired}
                />
              </DataCard>
            </DataColumn>
          </DataGrid>
        </Form>
      </div>
      <ConfirmationModal
        contentText='Do you want to Deactivate all related to language Promo Assets?'
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={handleConfirmationModalConfirm}
        onCancel={handleConfirmationModalCancel}
        visible={isConfirmationModalVisible}
      />
    </>
  )
}

const dataToSubmit = (
  values: Required<ReferralMaterialsDetailsFormValues>
): UpdateReferralMaterialDto => {
  return {
    name: values.name || '',
    caLanguages: values.caLanguages || [],
    typeId: values.typeId || 0,
    categoryIds: values.categoryIds || [],
    endDateTime: values.endDateTime
      ? createDateAsUTC(values.endDateTime, false, true).toISOString()
      : null,
    enabled: values.enabled || false,
    tickmillCompanyId: values?.tickmillCompanyId || 0,
    deactivatePromoAssets: values.shouldDeactivatePromoAssets,
  }
}

interface OuterProps {
  data: ReferralMaterialsDto
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  referralMaterialsTypes: NameDto[]
  referralMaterialsCategoriesByEntity: (NameDto<string> & { isActive: boolean })[]
  fetchReferralMaterialsCategoriesByEntity: (
    tickmillCompanyId: number | string,
    isCategoryActive?: boolean
  ) => Promise<(NameDto<string> & { isActive: boolean })[]>

  setAuditLogModal(value: boolean): void
  onSubmit(values: FormSubmitValues<UpdateReferralMaterialDto>): Promise<void>
}

export const ReferralMaterialsDetailsForm = withFormik<
  OuterProps,
  ReferralMaterialsDetailsFormValues
>({
  mapPropsToValues: (props) => {
    const { data } = props
    const categoryIds = (data.categories ?? []).map((category) => category.id)
    const isActive =
      data.enabled && (data.endDateTime == null || new Date(data.endDateTime) > new Date())

    return {
      name: data?.name || undefined,
      caLanguages: (data.caLanguages || []).map((x) => x.id),
      typeId: data?.type?.id,
      categoryIds,
      enabled: isActive,
      endDateTime: data.endDateTime ? convertDateToUTC(data.endDateTime).toISOString() : undefined,
      tickmillCompanyId: data?.tickmillCompany?.id || undefined,
      shouldDeactivatePromoAssets: false,
      activePromoAssetsLanguages: data.activePromoAssetsLanguages,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<ReferralMaterialsDetailsFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }

    if (!values.typeId) {
      errors.typeId = 'Required'
    }

    if (values.caLanguages.length <= 0) {
      errors.caLanguages = 'Required'
    }

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }

    if (isEmpty(values.enabled)) {
      errors.enabled = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
  validateOnMount: true,
})(ReferralMaterialsFormUI)
